/**
 * 에러 처리 유틸리티 함수
 *
 * mutation 에러 처리를 위한 공통 유틸리티입니다.
 * 중복되는 에러 메시지 추출 및 토스트 표시 패턴을 통합합니다.
 */

/**
 * unknown 타입 에러에서 메시지를 추출합니다.
 *
 * @param error - 에러 객체 (unknown 타입)
 * @param fallback - Error 인스턴스가 아닐 경우 사용할 기본 메시지
 * @returns 에러 메시지 문자열
 *
 * @example
 * const message = getErrorMessage(error, '저장 실패');
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * unknown 타입을 Error 인스턴스로 변환합니다.
 *
 * @param error - 에러 객체 (unknown 타입)
 * @returns Error 인스턴스
 *
 * @example
 * const err = toError(unknownError);
 * console.log(err.message);
 */
export function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * mutation onError 핸들러를 생성합니다.
 *
 * @param toastError - 에러 토스트 표시 함수
 * @param fallbackMessage - 기본 에러 메시지
 * @returns onError 콜백 함수
 *
 * @example
 * const mutation = useSomeMutation({
 *   mutation: {
 *     onError: createMutationErrorHandler(toastError, '저장 실패'),
 *   },
 * });
 */
export function createMutationErrorHandler(
  toastError: (message: string) => void,
  fallbackMessage: string
): (error: unknown) => void {
  return (error: unknown) => {
    toastError(getErrorMessage(error, fallbackMessage));
  };
}

/**
 * mutation 에러를 처리하고 토스트를 표시합니다.
 *
 * @param error - 에러 객체 (unknown 타입)
 * @param toastError - 에러 토스트 표시 함수
 * @param fallbackMessage - 기본 에러 메시지
 *
 * @example
 * onError: (error) => handleMutationError(error, toastError, '저장 실패'),
 */
export function handleMutationError(
  error: unknown,
  toastError: (message: string) => void,
  fallbackMessage: string
): void {
  toastError(getErrorMessage(error, fallbackMessage));
}

// ============================================================================
// Mutation Options Factory
// ============================================================================

/**
 * AppToast 함수 타입
 */
export interface ToastFunctions {
  success: (message: string) => void;
  error: (message: string) => void;
}

/**
 * Mutation 옵션 생성 설정
 */
export interface MutationOptionsConfig<TData = unknown> {
  /** 성공 시 표시할 메시지 */
  successMessage?: string;
  /** 실패 시 표시할 기본 메시지 */
  errorMessage: string;
  /** 성공 후 실행할 콜백 */
  onSuccess?: (data: TData) => void;
  /** 실패 후 실행할 콜백 */
  onError?: (error: Error) => void;
}

/**
 * TanStack Query mutation 옵션 반환 타입
 */
export interface MutationOptions<TData = unknown> {
  onSuccess: (data: TData) => void;
  onError: (error: unknown) => void;
}

/**
 * mutation 옵션을 생성합니다.
 * 성공/에러 핸들링을 통합하여 중복 코드를 제거합니다.
 *
 * @param toast - AppToast 함수들 (success, error)
 * @param config - Mutation 옵션 설정
 * @returns mutation 옵션 객체
 *
 * @example
 * const { success, error: toastError } = useToast();
 *
 * const deleteMutation = useChannelDestroy({
 *   mutation: createMutationOptions(
 *     { success, error: toastError },
 *     {
 *       successMessage: '채널이 삭제되었습니다.',
 *       errorMessage: '채널 삭제에 실패했습니다.',
 *       onSuccess: () => router.push('/channels'),
 *     }
 *   ),
 * });
 */
export function createMutationOptions<TData = unknown>(
  toast: ToastFunctions,
  config: MutationOptionsConfig<TData>
): MutationOptions<TData> {
  return {
    onSuccess: (data: TData) => {
      if (config.successMessage) {
        toast.success(config.successMessage);
      }
      config.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, config.errorMessage));
      if (config.onError) {
        config.onError(toError(error));
      }
    },
  };
}

